import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Send, Paperclip, X, CheckCircle, AlertCircle, Info, MessageSquare } from 'lucide-react';

export default function EmailModal({ isOpen, onClose, defaultTo = '', defaultSubject = '', defaultBody = '', appraisalId = '', defaultAttachPdf = false, defaultPdfType = 'quotation', markQuoteSent = false, onSent, smsMobile = '', smsDefaultMessage = '' }) {
  const { api } = useAuth();
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [attachPdf, setAttachPdf] = useState(defaultAttachPdf);
  const [pdfType, setPdfType] = useState(defaultPdfType);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // Batch S · "Also SMS client" — only surfaced when the parent passes a
  // truthy E.164 mobile. Default OFF so operators actively opt-in per send.
  const [alsoSms, setAlsoSms] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const canSms = Boolean(smsMobile);

  // Re-sync defaults when the modal is re-opened for a different action
  // (e.g. "Email quote" button populates To + Subject + the attach-PDF
  // pre-tick, then later the same modal could be opened for a generic
  // email with attach_pdf off).
  React.useEffect(() => {
    if (isOpen) {
      setTo(defaultTo);
      setSubject(defaultSubject);
      setBody(defaultBody);
      setAttachPdf(defaultAttachPdf);
      setPdfType(defaultPdfType);
      setResult(null);
      setAlsoSms(false);
      setSmsResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!to || !subject) return;
    setSending(true);
    setResult(null);
    setSmsResult(null);
    try {
      // Fire email + optional SMS in parallel — SMS failure never blocks the
      // email result, and vice versa. Both are surfaced separately below.
      const emailPromise = api.post('/email/send', {
        to_email: to, subject, body,
        attach_pdf: attachPdf, pdf_type: pdfType, appraisal_id: appraisalId,
        mark_quote_sent: markQuoteSent,
      });
      const smsPromise = (alsoSms && canSms && smsDefaultMessage)
        ? api.post('/sms/send', { to_number: smsMobile, message: smsDefaultMessage })
        : Promise.resolve(null);
      const [emailRes, smsRes] = await Promise.allSettled([emailPromise, smsPromise]);

      // Email side
      if (emailRes.status === 'fulfilled') {
        const data = emailRes.value?.data;
        setResult(data);
        if ((data?.status === 'sent' || data?.status === 'logged') && typeof onSent === 'function') {
          try { await onSent(data); } catch (_) { /* ignore */ }
        }
      } else {
        setResult({ status: 'failed', message: emailRes.reason?.response?.data?.detail || 'Failed to send' });
      }
      // SMS side (only if operator opted in)
      if (alsoSms && canSms) {
        if (smsRes.status === 'fulfilled' && smsRes.value) {
          setSmsResult(smsRes.value.data);
        } else if (smsRes.status === 'rejected') {
          setSmsResult({ status: 'failed', message: smsRes.reason?.response?.data?.detail || 'SMS failed' });
        }
      }
    } catch (e) {
      setResult({ status: 'failed', message: e.response?.data?.detail || 'Failed to send' });
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="email-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E2DF]">
          <h2 className="text-lg font-bold text-slate-900 font-[Manrope] flex items-center gap-2"><Send size={18} className="text-[#D35400]" /> Send Email</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">To *</label>
            <input data-testid="email-to-input" type="email" value={to} onChange={e => setTo(e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" placeholder="client@email.com" required />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Subject *</label>
            <input data-testid="email-subject-input" value={subject} onChange={e => setSubject(e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" required />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Message</label>
            <textarea data-testid="email-body-input" value={body} onChange={e => setBody(e.target.value)} rows={10} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
          </div>
          {appraisalId && (
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-md">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={attachPdf} onChange={e => setAttachPdf(e.target.checked)} className="rounded" />
                <Paperclip size={14} className="text-slate-400" /> Attach PDF Report
              </label>
              {attachPdf && (
                <select value={pdfType} onChange={e => setPdfType(e.target.value)} className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm">
                  <option value="quotation">Quotation Report</option>
                  <option value="materials">Materials Breakdown</option>
                  <option value="profit">Profit Analysis</option>
                </select>
              )}
            </div>
          )}
          {canSms && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-md border border-amber-100">
              <label className="flex items-center gap-2 text-sm" data-testid="also-sms-toggle-label">
                <input
                  data-testid="also-sms-checkbox"
                  type="checkbox"
                  checked={alsoSms}
                  onChange={e => setAlsoSms(e.target.checked)}
                  className="rounded"
                />
                <MessageSquare size={14} className="text-amber-700" />
                <span>Also SMS client at <span className="font-semibold">{smsMobile}</span></span>
              </label>
            </div>
          )}
          {result && (
            <div className={`flex items-center gap-2 p-3 rounded-md text-sm ${result.status === 'sent' ? 'bg-green-50 text-green-700 border border-green-200' : result.status === 'logged' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {result.status === 'sent' ? <CheckCircle size={16} /> : result.status === 'logged' ? <Info size={16} /> : <AlertCircle size={16} />}
              <span>Email: {result.message}</span>
            </div>
          )}
          {smsResult && (
            <div
              data-testid="also-sms-result-banner"
              className={`flex items-center gap-2 p-3 rounded-md text-sm ${smsResult.status === 'sent' ? 'bg-green-50 text-green-700 border border-green-200' : (smsResult.status === 'logged' || smsResult.status === 'skipped_international') ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
            >
              {smsResult.status === 'sent' ? <CheckCircle size={16} /> : (smsResult.status === 'logged' || smsResult.status === 'skipped_international') ? <Info size={16} /> : <AlertCircle size={16} />}
              <span>SMS: {smsResult.message}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm hover:bg-slate-50">Cancel</button>
            <button data-testid="send-email-button" onClick={handleSend} disabled={sending || !to || !subject} className="flex items-center gap-2 px-5 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold disabled:opacity-50 transition-all">
              <Send size={14} /> {sending ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
